import { Sprite } from 'pixi.js';
import { Weapon, EntityType, SelectionInfo } from '@astra/shared';
import { MovableEntity } from './base/MovableEntity';
import { degToRad } from '../utils/angleUtils';
import { getProjectileTexture, getBotProjectileTexture } from './ProjectileTextureFactory';

// Re-export for backwards compatibility
export { ensureProjectileTexturesExist, getProjectileTexture, ensureBotProjectileTexturesExist, getBotProjectileTexture } from './ProjectileTextureFactory';

// Unique ID counter for projectiles
let projectileIdCounter = 0;

/**
 * Unified projectile data interface.
 * Used for creating projectiles from network messages (bots, remote players).
 */
export interface ProjectileData {
  ownerId: string;
  x: number;
  y: number;
  angle: number; // In radians
  damage: number;
  speed: number;
  range?: number; // Optional, defaults to 800
  weaponType: 'laser' | 'kinetic' | 'missile' | 'energy' | 'explosive';
  isHostile?: boolean; // If true, uses bot (red) textures; if false, uses player (teal) textures

  // Missile tracking properties (only for weaponType === 'missile')
  targetId?: string;           // ID of target entity (if tracking)
  targetType?: 'bot' | 'player' | 'asteroid' | 'drone';
  trackingSpeed?: number;      // Turn rate in degrees per second
  lifetime?: number;           // Self-destruct time in ms
  trackingRange?: number;      // Maximum range for tracking to activate
}

/**
 * Function type for getting target position.
 * Used by missiles to track their target.
 */
export type TargetPositionGetter = (id: string, type: string) => { x: number; y: number } | null;

/**
 * Projectile - A moving projectile fired by players or bots.
 *
 * Extends: MovableEntity
 *
 * Features:
 * - Moves in a straight line based on initial velocity
 * - Has limited range (max distance traveled)
 * - Cannot be selected/targeted (selectionRadius = 0)
 * - Hostility determines texture color scheme (player=teal, bot=red)
 *
 * Creation methods:
 * 1. Local player: `Projectile.fromWeapon(x, y, angle, weapon, playerId, shipVx, shipVy)`
 * 2. Network (bot/remote): `new Projectile(data)`
 */
export class Projectile extends MovableEntity {
  // Required abstract properties from SpaceEntity
  readonly id: string;
  readonly entityType: EntityType = 'projectile';

  // Required abstract properties from TargetableEntity
  // Projectiles are not normally selectable, so use 0 radius
  readonly selectionRadius: number = 0;
  // Hostile depends on owner - set based on who fired it
  readonly isHostile: boolean;

  // Projectile-specific properties
  public damage: number;
  public range: number;
  private distanceTraveled: number = 0;
  private startX: number;
  private startY: number;
  public ownerId: string; // Owner of the projectile (playerId or botId)

  // Collision radius
  public readonly collisionRadius: number = 3;

  // Visual sprite (child of this container)
  private sprite: Sprite;

  // Missile tracking properties
  private targetId: string | null = null;
  private targetType: 'bot' | 'player' | 'asteroid' | 'drone' | null = null;
  private trackingSpeed: number = 0;       // Radians per second
  private lifetime: number = Infinity;      // Self-destruct time in ms
  private trackingRange: number = Infinity;
  private age: number = 0;                  // Time since creation in ms
  private isMissile: boolean = false;
  private baseSpeed: number = 0;            // Store base speed for tracking

  // Callback to get target position (set by GameScene for missiles)
  private getTargetPosition: TargetPositionGetter | null = null;

  /**
   * Create a projectile from explicit data (used for network projectiles from bots/remote players).
   */
  constructor(data: ProjectileData) {
    super();

    // Generate unique ID
    this.id = `projectile-${Date.now()}-${projectileIdCounter++}`;

    // Set hostility based on data (default to hostile for backwards compatibility with bot projectiles)
    this.isHostile = data.isHostile ?? true;

    this.ownerId = data.ownerId;
    this.damage = data.damage;
    this.range = data.range ?? 800;
    this.startX = data.x;
    this.startY = data.y;

    // Set position on the container
    this.position.set(data.x, data.y);

    // Get texture based on hostility (hostile = bot red colors, friendly = player teal colors)
    const texture = this.isHostile
      ? getBotProjectileTexture(data.weaponType)
      : getProjectileTexture(data.weaponType);

    // Create and add the sprite as a child
    this.sprite = new Sprite(texture);
    this.sprite.anchor.set(0.5);
    this.addChild(this.sprite);

    // Set rotation to match direction (angle already in radians)
    this.rotation = data.angle;

    // Calculate velocity based on angle and speed
    this.velocityX = Math.cos(data.angle) * data.speed;
    this.velocityY = Math.sin(data.angle) * data.speed;

    // Set depth to be in front of ships
    this.zIndex = 10;

    // Missile tracking setup
    if (data.weaponType === 'missile') {
      this.isMissile = true;
      this.targetId = data.targetId ?? null;
      this.targetType = data.targetType ?? null;
      // Convert tracking speed from degrees/sec to radians/sec
      this.trackingSpeed = ((data.trackingSpeed ?? 90) * Math.PI) / 180;
      this.lifetime = data.lifetime ?? 3000;
      this.trackingRange = data.trackingRange ?? 500;
      this.baseSpeed = data.speed;
    }
  }

  /**
   * Factory method: Create a projectile from a weapon (used by local player).
   * This handles the conversion from degrees to radians and adds ship velocity.
   * For missiles, targetId and targetType enable homing behavior.
   */
  static fromWeapon(
    x: number,
    y: number,
    angleDegrees: number,
    weapon: Weapon,
    playerId: string,
    shipVelocityX: number = 0,
    shipVelocityY: number = 0,
    targetId?: string,
    targetType?: 'bot' | 'player' | 'asteroid' | 'drone'
  ): Projectile {
    const angleRadians = degToRad(angleDegrees);
    const speed = weapon.stats.projectileSpeed;

    // Create projectile data
    const data: ProjectileData = {
      ownerId: playerId,
      x,
      y,
      angle: angleRadians,
      damage: weapon.stats.damage,
      speed,
      range: weapon.stats.range,
      weaponType: weapon.weaponType,
      isHostile: false, // Player projectiles are not hostile (from local player's perspective)

      // Missile tracking data (only used if weaponType === 'missile')
      targetId: weapon.weaponType === 'missile' ? targetId : undefined,
      targetType: weapon.weaponType === 'missile' ? targetType : undefined,
      trackingSpeed: weapon.stats.trackingSpeed,
      lifetime: weapon.stats.lifetime,
      trackingRange: weapon.stats.trackingRange,
    };

    const projectile = new Projectile(data);

    // Missiles don't inherit ship lateral velocity — they launch clean along their heading.
    // Kinetic projectiles still inherit full ship momentum for realistic physics.
    if (weapon.weaponType !== 'missile') {
      projectile.velocityX += shipVelocityX;
      projectile.velocityY += shipVelocityY;
    }

    return projectile;
  }

  /**
   * Set the target position getter function (called by GameScene for missiles).
   * This allows the projectile to track moving targets.
   */
  setTargetPositionGetter(getter: TargetPositionGetter): void {
    this.getTargetPosition = getter;
  }

  /**
   * Update projectile position. Returns false if projectile should be destroyed.
   * Note: Projectiles use direct position updates, not interpolation.
   * Missiles have additional tracking behavior and lifetime limits.
   */
  update(delta: number): boolean {
    const dt = delta / 1000;

    // Track age for missile lifetime check
    this.age += delta;

    // Check missile lifetime - self-destruct if expired
    if (this.isMissile && this.age >= this.lifetime) {
      return false;
    }

    // Missile tracking logic
    if (this.isMissile && this.targetId && this.targetType && this.getTargetPosition) {
      const targetPos = this.getTargetPosition(this.targetId, this.targetType);

      if (targetPos) {
        const dx = targetPos.x - this.x;
        const dy = targetPos.y - this.y;
        const distToTarget = Math.sqrt(dx * dx + dy * dy);

        if (distToTarget <= this.trackingRange) {
          const targetAngle = Math.atan2(dy, dx);
          const currentAngle = Math.atan2(this.velocityY, this.velocityX);

          // Shortest-path angle difference
          let angleDiff = targetAngle - currentAngle;
          while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
          while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

          // Close-range boost: when within 200u, up to 4x turn rate so missiles
          // lock hard onto the target instead of flying past.
          const closeRange = 200;
          const closeFactor = distToTarget < closeRange
            ? 1 + 3 * (1 - distToTarget / closeRange)
            : 1;

          const maxTurn = this.trackingSpeed * closeFactor * dt;
          const turn = Math.max(-maxTurn, Math.min(maxTurn, angleDiff));

          const newAngle = currentAngle + turn;
          const currentSpeed = Math.sqrt(this.velocityX * this.velocityX + this.velocityY * this.velocityY);
          this.velocityX = Math.cos(newAngle) * currentSpeed;
          this.velocityY = Math.sin(newAngle) * currentSpeed;

          this.rotation = newAngle;
        }
      }
    }

    // Move based on velocity (direct update, no interpolation)
    this.x += this.velocityX * dt;
    this.y += this.velocityY * dt;

    // Calculate distance traveled
    const dx = this.x - this.startX;
    const dy = this.y - this.startY;
    this.distanceTraveled = Math.sqrt(dx * dx + dy * dy);

    // Return false if out of range (signal to destroy)
    if (this.distanceTraveled >= this.range) {
      return false;
    }

    return true;
  }

  /**
   * Get selection info for this projectile.
   * Projectiles aren't normally selected, but we implement this for completeness.
   */
  getSelectionInfo(): SelectionInfo {
    return {
      type: this.entityType,
      id: this.id,
      name: 'Projectile',
      x: this.x,
      y: this.y,
      data: {
        damage: this.damage,
        ownerId: this.ownerId,
      },
    };
  }

  // Check if projectile hits a target at given position with radius
  public checkHit(targetX: number, targetY: number, radius: number): boolean {
    const dx = targetX - this.x;
    const dy = targetY - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    return dist <= radius + this.collisionRadius;
  }

  /**
   * Cleanup resources when projectile is destroyed.
   */
  destroy(): void {
    this.sprite.destroy();
    super.destroy();
  }
}

// Backwards compatibility aliases
export { Projectile as BotProjectile };
export type BotProjectileData = ProjectileData;
